// vendors
import React, { useContext, useMemo, useState } from 'react';

// commons
import { Checkbox, Modal } from 'lego-dist';

// hooks
import { useTranslation } from 'react-i18next';
import { useRouter } from 'routes';
import useTeacher from 'hooks/useTeacher';
import useCenters from 'hooks/centers/UseCenters';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import { AppContext } from 'App';

// styles
import {
  SearchBar,
  SearchIcon,
  CenterCount,
  CenterHeader,
  PrimaryButton,
  CenterActions,
  PrimaryButtonMobile,
} from './styles';

/* Components */
import AddTeacherView from 'views/NewAddTeacherView';
import AddTeacherSuccess from 'views/NewAddTeacherView/SuccessView';
import { MoveStudentsModal } from '../MoveStudentsModal';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';
import SelectAllCheckbox from '../sharedComponents/styles/SelectAllCheckbox';

import memberAddedSuccessImage from 'images/checked.svg';
import {
  SuccessModalImage,
  SuccessModalTitle,
} from '../MoveStudentsModal/MoveStudentsModal.styled';
import { ROLES_OBJECT } from 'constants/onBoarding';
import themes from 'themes';

const defaultButtonProps = {
  flat: true,
  rounded: true,
  color: '#75B753',
};

const secondaryButtonProps = {
  fill: true,
  flat: true,
  rounded: true,
  color: '#0086D8',
};

interface IClassroomDetailsActionsProps {
  data?: Array<{}>;
  countText?: any;
  classroom?: any;
  onSearch?: Function;
  selectedAll?: boolean;
  onSelectAll?: Function;
  showSearcher?: boolean;
  selectedItems?: object;
  onMovedItems?: Function;
  primaryButtonProps?: any;
  withSelectAllCheckbox?: boolean;
  refreshTeacherList?: Function;
}

// eslint-disable-next-line import/prefer-default-export
export function ClassroomDetailsActions({
  data = [],
  classroom,
  countText = '',
  selectedItems = {},
  onSearch = () => {},
  selectedAll = false,
  showSearcher = false,
  onSelectAll = () => {},
  onMovedItems = () => {},
  primaryButtonProps = {},
  withSelectAllCheckbox = false,
  refreshTeacherList = () => {},
}: IClassroomDetailsActionsProps) {
  const { t: translate, i18n } = useTranslation();
  const { currentParamsRef, history } = useRouter();
  const {
    current: { centerId },
  } = currentParamsRef;

  /* open modals */
  const [showMovedSuccessModal, setShowMovedSuccessModal] = useState(false);
  const [openAddTeacherModal, setOpenAddTeacherModal] = useState(false);
  const [openMoveStudentModal, setOpenMoveStudentModal] = useState(false);
  const [openAddTeacherSuccess, setOpenAddTeacherSuccess] = useState(false);

  const onSearchBarChange = (value: string) => {
    const pattern = new RegExp(`${value}`, 'i');
    const filteredData = data.filter(
      (item: any) => item.name && pattern.test(item.name),
    );
    onSearch(filteredData);
  };

  /* ADD STUDENT functions */
  const handleAddStudent = () => {
    const currentPath = history.location.pathname.replace('/', '');
    return history.push(
      `${APP_SECTIONS_PATHS.ADD_STUDENTS}?prevView=${currentPath}&groupId=${classroom?.id}&publicId=${classroom?.public_id}&groupName=${classroom?.name}`,
    );
  };

  /* ADD TEACHER functions */
  const { addTeacher } = useTeacher();
  const { fetchCenterById } = useCenters();
  const { isFreemiumUser, isDemoUser } = useContext(AppContext);

  const handleAddTeacherFlow = () => {
    if (isFreemiumUser || isDemoUser) {
      history.push(APP_SECTIONS_PATHS.PREMIUM_PROCESS);
    } else {
      setOpenAddTeacherModal(true);
    }
  };

  const handleOnFinishFlow = (values: any) => {
    const {
      general_info: generalInfo,
      permissions,
      centersWithClassrooms,
    } = values;

    const form = {
      user: {
        permission_ids: permissions.permissions,
        role_alias: permissions.role_alias,
        center_ids: centersWithClassrooms.centers.map((center: any) => typeof (center) === 'number' ? center : center.id).filter((center: any) => !!center),
        group_ids: centersWithClassrooms?.classrooms?.map((clsr: any) => typeof (clsr) === 'number' ? clsr : clsr.id),
        ...generalInfo,
        type: ROLES_OBJECT.TEACHER.value,
        title: ROLES_OBJECT.TEACHER.value,
        locale: i18n.language,
      },
    };

    addTeacher(form, centerId)
      .then(() => {
        fetchCenterById(centerId);
        if (refreshTeacherList) {
          refreshTeacherList();
        }
      })
      .finally(() => {
        setOpenAddTeacherModal(false);
        setOpenAddTeacherSuccess(true);
        setTimeout(() => setOpenAddTeacherSuccess(false), 3000);
      });
  };


  const handleClick = () => {
    if (primaryButtonProps?.tab === 'General') {
      if (showSearcher) {
        handleAddStudent();
      } else {
        setOpenMoveStudentModal(true);
      }
    } else {
      handleAddTeacherFlow();
    }
  };

  const renderMovedSuccessModal = () => (
    <ResultActionModal
      image={memberAddedSuccessImage}
      title={translate('settings:MOVED_STUDENTS')}
      configPopsModal={{
        onClosedModal: () => setShowMovedSuccessModal(!showMovedSuccessModal),
      }}
      styles={{ Image: SuccessModalImage, Title: SuccessModalTitle }}
    />
  );

  const buttonProps = showSearcher ? defaultButtonProps : secondaryButtonProps;

  const hasSomeItemsChecked = useMemo(() => {
    const selectedItemsLength = Object.keys(selectedItems).length;
    const hasCheckedAtLeastAnItem = selectedItemsLength > 0;

    return hasCheckedAtLeastAnItem && !selectedAll;
  }, [selectedItems, selectedAll]);

  return (
    <CenterHeader>
      {showMovedSuccessModal ? renderMovedSuccessModal() : <></>}
      <CenterCount>
        {withSelectAllCheckbox ? (
          <SelectAllCheckbox someChecked={hasSomeItemsChecked}>
            <Checkbox
              label=" "
              name="selectAll"
              checked={selectedAll}
              onChange={onSelectAll}
            />
          </SelectAllCheckbox>
        ) : (
          <></>
        )}
        {countText}
      </CenterCount>
      <CenterActions>
        <SearchBar
          showSearcher={showSearcher}
          onChange={onSearchBarChange}
          rightSlot={() => <SearchIcon />}
          cleanInputValue={data.length === 0}
          placeholder={translate('settings:STAFF_SECTION.MANAGE.SEARCH')}
        />
        <PrimaryButtonMobile
          {...buttonProps}
          onClick={primaryButtonProps?.onClickPrimaryButton}
        >
          {primaryButtonProps?.mobileText || '+'}
        </PrimaryButtonMobile>
        <PrimaryButton {...buttonProps} onClick={() => handleClick()}>
          {primaryButtonProps?.text}
        </PrimaryButton>
      </CenterActions>
      <Modal
        openModal={openAddTeacherModal}
        title={translate('settings:ADD_TEACHER')}
        headerColor={themes.colors.newKinedu}
        closeButtonColor="#ffffff"
        confirmCloseModal
        confirmCloseModalTexts={{
          title: translate('activities:IF_YOU_LEAVE.TITLE'),
          button_leave: translate('activities:IF_YOU_LEAVE.BUTTON_LEAVE'),
          button_stay: translate('activities:IF_YOU_LEAVE.BUTTON_STAY'),
          description: translate('activities:IF_YOU_LEAVE.DESCRIPTION'),
          warning: translate('activities:IF_YOU_LEAVE.WARNING'),
        }}
        persistent
        onClosedModal={() => {
          setOpenAddTeacherModal(false);
        }}
      >
        {() => <AddTeacherView onFinishFlow={handleOnFinishFlow} />}
      </Modal>
      <Modal
        openModal={openAddTeacherSuccess}
        onClosedModal={() => {
          setOpenAddTeacherSuccess(false);
        }}
      >
        {() => (
          <AddTeacherSuccess
            caption={translate(
              'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.MODAL_RESULT.TEACHER',
            )}
          />
        )}
      </Modal>
      <MoveStudentsModal
        currentClassroom={classroom}
        selectedStudents={selectedItems}
        showModal={openMoveStudentModal}
        setShowModal={setOpenMoveStudentModal}
        onMovedStudents={() => {
          onMovedItems();
          setShowMovedSuccessModal(true);
        }}
      />
    </CenterHeader>
  );
}
